import { useState, useEffect } from "react";
import Input from "../ui/Input";
import { Pen, Trash2 } from "lucide-react";
import Button from "../ui/Button";
import { useUpdatePayroll, useDeletePayroll } from "../Use-auth";

const getTodayDate = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

export default function PayRollSalary({ salary, employeeId, username, workEmail, personalEmail, annual }) {
    const [showDetails, setShowDetails] = useState(false);
    const [salaryType, setSalaryType] = useState("starting");
    const [salaryDate, setSalaryDate] = useState(getTodayDate());
    const [deletingId, setDeletingId] = useState(null);

    const [localSalary, setLocalSalary] = useState({
        annualCTC: '',
        monthlyCTC: '',
        basic: '',
        allowances: '',
    });

    const { mutate: updateUser, isPending: isUpdating } = useUpdatePayroll();
    const { mutate: deletePayroll, isPending: isDeleting } = useDeletePayroll();

    useEffect(() => {
        if (salary?.annualCtc) {
            const monthly = salary.annualCtc / 12;

            setLocalSalary({
                annualCTC: Number(salary.annualCtc),
                monthlyCTC: monthly,
                basic: monthly,
                allowances: 0,
            });
        }
    }, [salary]);

    const handleChange = (field, value) => {
        const num = Number(value) || 0;

        setLocalSalary((prev) => {
            if (field === "annualCTC") {
                const monthly = num / 12;
                return {
                    ...prev,
                    annualCTC: num,
                    monthlyCTC: monthly,
                    basic: monthly,
                };
            }

            return { ...prev, [field]: num };
        });
    };

    const totalMonthly =
        (localSalary.basic || 0) + (localSalary.allowances || 0);

    const handleSave = () => {
        if (!employeeId) {
            alert("Employee ID is missing");
            return;
        }

        if (!localSalary.annualCTC || localSalary.annualCTC <= 0) {
            alert("Please enter a valid Annual CTC");
            return;
        }

        if (!localSalary.basic || localSalary.basic <= 0) {
            alert("Please enter Basic salary");
            return;
        }

        if (totalMonthly <= 0) {
            alert("Total salary must be greater than 0");
            return;
        }

        if (!salaryDate) {
            alert("Please select salary date");
            return;
        }

        if (!salaryType) {
            alert("Please select salary Type");
            return;
        }

        updateUser(
            {
                employeeId,
                payroll: [
                    {
                        userId: employeeId,
                        username: username,
                        email: [workEmail, personalEmail],
                        annualCtc: localSalary.annualCTC,
                        type: salaryType,
                        createdDate: salaryDate,
                    },
                ],
            },
            {
                onSuccess: () => {
                    alert("Salary updated successfully!");
                    setShowDetails(false);
                },
                onError: () => alert("Error updating salary"),
            }
        );
    };

    const handleDelete = (item) => {
        if (!employeeId) {
            alert("Employee ID is missing");
            return;
        }

        const confirmDelete = window.confirm(
            "Are you sure you want to delete this salary entry?"
        );
        if (!confirmDelete) return;

        const recordId = item._id || item.id;
        setDeletingId(recordId);

        deletePayroll(
            {
                employeeId,
                 payrollId: recordId, 
            },
            {
                onSuccess: () => {
                    alert("Salary entry deleted successfully!");
                    setDeletingId(null);
                },
                onError: () => {
                    alert("Error deleting salary entry");
                    setDeletingId(null);
                },
            }
        );
    };

    const formatAmount = (amount) =>
        Math.round(amount).toLocaleString();

    const formatDate = (date) => {
        if (!date) return "-";
        const d = new Date(date);
        return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
    };

    return (
        <>
            <div className="p-2 rs:p-4 border rounded shadow-sm bg-white mx-auto mb-3 sm:mb-10">
                <div className="flex gap-2 mb-2 items-center">
                    <h2 className="text-[15px] sm:text-[20px] font-bold">Salary Details</h2>
                    <Pen
                        size={16}
                        className="text-blue-500 cursor-pointer"
                        onClick={() => setShowDetails((s) => !s)}
                    />
                </div>

                <div className="flex gap-[50px] items-center  mb-1 sm:mb-4 bg-gray-50 p-1 sm:p-3 rounded">
                    <div>
                        <p className="text-sm text-gray-600">Monthly CTC</p>
                        <p className="text-[12px] sm:text-[16px]">
                            ₹ {formatAmount(localSalary.monthlyCTC)}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-600">Annual CTC</p>
                        <p className="text-[12px] sm:text-[16px]">
                            ₹ {formatAmount(localSalary.annualCTC)}
                        </p>
                    </div>
                </div>

                {showDetails && (
                    <>
                        <div className="mb-4 flex gap-4 items-center text-sm">
                            Annual CTC:
                            <div>
                                <Input
                                    type="number"
                                    value={localSalary.annualCTC}
                                    onChange={(e) =>
                                        handleChange("annualCTC", e.target.value)
                                    }
                                />
                            </div>
                            / year
                        </div>

                        <div className="bg-white p-3 rounded border flex flex-col sm:flex-row sm:items-center gap-3 text-sm mt-4">
                            <div className="flex-1">Select Date</div>

                            <div className="flex-1">
                                <input
                                    type="date"
                                    value={salaryDate}
                                    onChange={(e) => setSalaryDate(e.target.value)}
                                    className="w-full border rounded px-2 py-2"
                                />
                            </div>
                            <div className="flex-1"></div>
                        </div>
                        <div className="bg-white p-3 rounded border flex flex-col sm:flex-row sm:items-center gap-3 text-sm mt-4">
                            <div className="flex-1">Type</div>
                            <div className="flex-1">
                                <select
                                    value={salaryType}
                                    onChange={(e) => setSalaryType(e.target.value)}
                                    className="w-full border rounded px-2 py-2"
                                >
                                    <option value="starting">Starting Salary</option>
                                    <option value="increment">Increment</option>
                                    <option value="decrement">Decrement</option>
                                </select>
                            </div>
                            <div className="flex-1"></div>

                        </div>

                        <div className="flex justify-end ">
                            <Button
                                onClick={handleSave}
                                disabled={isUpdating}
                                variant="success"
                                className="mt-[10px] min-w-[100px]"
                            >
                                {isUpdating ? (
                                    <div className="flex items-center gap-2 justify-center">
                                        Saving...
                                        <span className="loader-circle"></span>
                                    </div>
                                ) : (
                                    "Save"
                                )}
                            </Button>
                        </div>
                    </>
                )}
            </div>

            <div className="overflow-x-auto p-1 rs:p-4 border">
                <table className="w-full border text-[12px] sm:text-sm">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="border p-1 rs:p-2">Date</th>
                            <th className="border p-1 rs:p-2">Type</th>
                            <th className="border p-1 rs:p-2">Annual CTC</th>
                            <th className="border p-1 rs:p-2">Monthly CTC</th>
                            <th className="border p-1 rs:p-2">Action</th>
                        </tr>
                    </thead>

                    <tbody>
                        {[...annual]
                            .reverse()
                            .filter((item) => Number(item.netSalary || 0) === 0)
                            .map((item, index) => {
                                const recordId = item._id || item.id;
                                const isRowDeleting = isDeleting && deletingId === recordId;

                                return (
                                    <tr key={index} className="border text-center">
                                        <td>{formatDate(item.createdDate)}</td>

                                        <td className="border p-1 rs:p-2">
                                            {item.type || "-"}
                                        </td>

                                        <td className="border p-1 rs:p-2">
                                            ₹ {formatAmount(Number(item.annualCtc || 0))}
                                        </td>

                                        <td className="border p-1 rs:p-2 font-semibold text-green-700">
                                            ₹ {formatAmount(Number(item.annualCtc || 0) / 12)}
                                        </td>

                                        <td className="border p-1 rs:p-2">
                                            <button
                                                onClick={() => handleDelete(item)}
                                                disabled={isRowDeleting}
                                                className="text-red-500 hover:text-red-700 disabled:opacity-50 cursor-pointer inline-flex items-center justify-center"
                                                title="Delete"
                                            >
                                                {isRowDeleting ? (
                                                    <span className="loader-circle w-4 h-4"></span>
                                                ) : (
                                                    <Trash2 size={16} />
                                                )}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>

                </table>
            </div>
        </>
    );
}